import { useState } from "react"
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"
import { CustomSpin } from "components/custom/Loader"

interface Colleague {
  value: string
  label: string
  initials: string
}

interface SelectColleagueProps {
  colleagues: Colleague[]
  value: string
  onChange: (value: string) => void
  search: string
  onSearch: (value: string) => void
  isLoadingColleaguesData?: boolean
}

const SelectColleague = ({
  colleagues,
  value,
  onChange,
  search,
  onSearch,
  isLoadingColleaguesData,
}: SelectColleagueProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const filteredColleagues = colleagues
    .filter((c) => c.label.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))

  return (
    <div className="relative">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center border border-slate-300 rounded-md px-3 py-2 cursor-pointer"
      >
        <MagnifyingGlassIcon
          width={15}
          height={15}
          className="text-slate-400 shrink-0 mr-2"
        />
        {value ? (
          <span className="flex-1 text-sm text-padeBlack">
            {colleagues.find((c) => c.value === value)?.label}
          </span>
        ) : (
          <input
            type="text"
            placeholder="Search for a colleague"
            value={search}
            required
            onChange={(e) => {
              onSearch(e.target.value)
              setIsOpen(true)
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 text-sm outline-none border-none focus:ring-0 bg-transparent"
          />
        )}
        <ChevronDownIcon
          width={15}
          height={15}
          className={`text-slate-400 shrink-0 ml-2 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full border border-slate-300 rounded-md mt-1 bg-white overflow-hidden shadow-md">
          <div className="max-h-40 overflow-y-auto">
            {isLoadingColleaguesData ? (
              <div className="py-3 flex justify-center">
                <CustomSpin spinning />
              </div>
            ) : null}
            {!isLoadingColleaguesData &&
              filteredColleagues.map((c) => (
                <label
                  key={c.value}
                  onClick={() => {
                    onChange(c.value)
                    onSearch("")
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="colleague"
                    value={c.value}
                    checked={value === c.value}
                    onChange={() => onChange(c.value)}
                    className="accent-blue-600"
                  />
                  <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 text-xs font-semibold flex items-center justify-center shrink-0">
                    {c.initials}
                  </span>
                  <span className="text-sm">{c.label}</span>
                </label>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SelectColleague
